import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TextField from 'components/text-field';
import React, { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyAccounting } from 'types/ledger';

type PartyPickerProps = {
  parties: PartyAccounting[];
  selectedPartyId: string | null | undefined;
  onSelect: (party: PartyAccounting) => void;
  filterParties?: string;
  disabled?: boolean;
  /** Stretch the picker to the height of its container (mobile step) instead of a fixed cap. */
  fillHeight?: boolean;
};

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  fill: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  listFrame: {
    border: `1px solid ${theme.palette.border.line}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  listFrameFill: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  list: {
    maxHeight: theme.spacing(40),
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: theme.palette.background.paper,
  },
  listFill: {
    maxHeight: 'none',
    flex: '1 1 auto',
    minHeight: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `${theme.spacing(0.5)} solid transparent`,
    cursor: 'pointer',
    outline: 'none',
    transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shortest }),
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  rowSelected: {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    '&:hover': { backgroundColor: theme.palette.action.selected },
  },
  rowDisabled: {
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
  },
  kinds: {
    color: theme.palette.text.secondary,
    overflowWrap: 'anywhere',
  },
  textDisabled: { color: theme.palette.text.disabled },
  check: { color: theme.palette.primary.main },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2),
  },
  emptyText: { color: theme.palette.text.secondary },
}));

const formatKinds = (party: PartyAccounting) => party.kinds
  .reduce((roles: string[], kind) => {
    const linked = party.associations?.filter((association) => association.linked_as === kind) || [];
    if (!linked.length) return [...roles, kind];
    return [...roles, ...linked.map((association) => `${kind} of ${association.party_kind}`)];
  }, [])
  .join(', ');

const matchesQuery = (party: PartyAccounting, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (party.name || '').toLowerCase().includes(needle)
    || party.kinds.some((kind) => kind.toLowerCase().includes(needle))
    || formatKinds(party).toLowerCase().includes(needle);
};

const PartyPicker = ({
  parties,
  selectedPartyId,
  onSelect,
  filterParties,
  disabled = false,
  fillHeight = false,
}: PartyPickerProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const kindParties = filterParties
    ? parties.filter((party) => party.kinds.includes(filterParties))
    : parties;
  const visibleParties = kindParties.filter((party) => matchesQuery(party, query));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, party: PartyAccounting) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(party);
    }
  };

  return (
    <Box className={fillHeight ? `${classes.root} ${classes.fill}` : classes.root}>
      <TextField
        label={t('common:filter')}
        value={query}
        disabled={disabled}
        onChange={(event) => setQuery(event.target.value)}
        inputProps={{
          'aria-label': t('common:search'),
          'aria-controls': 'party-picker-list',
          autoComplete: 'off',
          onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') event.preventDefault();
          },
        }}
      />
      <Box className={fillHeight ? `${classes.listFrame} ${classes.listFrameFill}` : classes.listFrame}>
        <div
          id="party-picker-list"
          className={fillHeight ? `${classes.list} ${classes.listFill}` : classes.list}
          role="radiogroup"
          aria-label={t('parties:contact')}
          aria-disabled={disabled || undefined}
        >
          {visibleParties.length > 0 ? (
            visibleParties.map((party) => {
              const isSelected = party._id === selectedPartyId;
              return (
                <div
                  key={party._id}
                  role="radio"
                  aria-checked={isSelected}
                  aria-disabled={disabled || undefined}
                  tabIndex={disabled ? -1 : 0}
                  className={[
                    classes.row,
                    isSelected ? classes.rowSelected : '',
                    disabled ? classes.rowDisabled : '',
                  ].join(' ')}
                  onClick={() => {
                    if (!disabled) onSelect(party);
                  }}
                  onKeyDown={(event) => handleKeyDown(event, party)}
                >
                  <div className={classes.rowText}>
                    <Typography variant="body2" className={`${classes.name} ${disabled ? classes.textDisabled : ''}`}>
                      {party.name}
                    </Typography>
                    {party.kinds.length > 0 && (
                      <Typography variant="caption" className={`${classes.kinds} ${disabled ? classes.textDisabled : ''}`}>
                        {formatKinds(party)}
                      </Typography>
                    )}
                  </div>
                  {isSelected && <FontAwesomeIcon icon="check" className={classes.check} aria-hidden="true" />}
                </div>
              );
            })
          ) : (
            <div className={classes.empty}>
              <Typography variant="body2" className={classes.emptyText}>
                {t('parties:noParties')}
              </Typography>
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default PartyPicker;
